import detectEthereumProvider from '@metamask/detect-provider';
import { BrowserProvider, ethers } from 'ethers';

import { getCurrentStateChainConfig } from 'config/configUtils';
import { REJECT_WALLET_PROMPT_ERROR } from 'lib/ethereum/ethersErrors';
import { staticDispatch } from 'reduxState/store';
import {
  changeWalletChainId,
  updatePendingWalletAddress,
} from 'reduxState/wallet/walletDetailsReducer';
import { WalletType } from 'reduxState/wallet/walletUtils';

import { getNetworkChainName } from '../chains';

// Main web3 from software wallet
// let web3WalletType:
let web3:
  | {
      provider: BrowserProvider;
      walletType: WalletType;
    }
  | undefined;

export const detectSoftwareWalletChainId = async (): Promise<
  undefined | number
> => {
  if (web3 && web3.provider?.getNetwork) {
    try {
      const network = await web3.provider.getNetwork();
      return Number(network.chainId);
    } catch (error) {
      console.warn(error);
    }
  }
  // Alternative old ways - just in case
  return (
    web3?.provider._network?.chainId ||
    (web3?.provider as any).network?.chainId ||
    (web3?.provider as any)?.version?.network ||
    (window.ethereum as any)?.networkVersion
  );
};

// Prefer wallet specific ETH provider so user can run multiple wallets at once
async function getEthereumProvider(_walletType: WalletType) {
  const provider = await detectEthereumProvider();
  return provider;
  // return walletType === 'softwareWeb3_bitKeep' && window?.bitkeep?.ethereum
  //   ? window?.bitkeep?.ethereum
  //   : await detectEthereumProvider();
}

/**
 * Check whenever user is not running Metamask in Kovan,
 * but IDEX is running on Mainnet. Or vice versa.
 */
const checkAndAlertWeb3NetworkCompatibility = async () => {
  const web3ChainNetwork = await detectSoftwareWalletChainId();
  // Do check only if web3 version is known
  if (!web3ChainNetwork) {
    console.warn('[web3] Unknown web3 network');
    return true;
  }
  const { chainId: currentChainId } = getCurrentStateChainConfig();
  if (`${web3ChainNetwork}` === `${currentChainId}`) {
    return true;
  }
  // const appNetworkName = getNetworkChainName(currentChainId);
  const web3NetworkName = getNetworkChainName(web3ChainNetwork);
  console.log('[web3] version.network', web3ChainNetwork, web3NetworkName);
  // console.warn(
  //   `WARNING: ${web3NetworkName} detected, but IDEX require ${appNetworkName}!` +
  //     `\n\n` +
  //     `Change Ethereum network on your web3 wallet provider (eg. Metamask Software wallet). ` +
  //     `IDEX is running on ${appNetworkName}, but you are runninng ${web3NetworkName}. ` +
  //     `This might cause IDEX to not work properly.`
  // );
  return false;
};

/**
 * Stop existing web3 instance listener and get fresh web3.
 * Usefull for wallet unlock when we need to get fresh web3 instance (eg. changed network/chain).
 */
export const resetWeb3 = async () => {
  if (web3) {
    web3.provider.removeAllListeners();
  }
  web3 = undefined;
};

/**
 * Warning: This might not be available immediatelly
 * because ethereum provider can be injected later.
 * Use only in async functions, like wallet unlock.
 */
// export const detectAvailableWeb3Wallet = ():
//   | 'softwareWeb3'
//   | 'bitkeep'
//   | 'trustWallet'
//   | 'tokenPocket'
//   | false => {
//   // TokenPocket DApp browser seems to expose also isTrust and isMetaMask,
//   // so this must be first
//   if ((window?.ethereum as any)?.isTokenPocket) {
//     return 'tokenPocket';
//   }
//   // https://docs.bitkeep.com/guide/how-to-connect.html
//   // https://docs.bitkeep.com/en/docs/guide/wallet/faq.html
//   if (window?.isBitKeep || window?.bitkeep?.ethereum) {
//     return 'bitkeep';
//   }
//   // https://github.com/trustwallet/trust-web3-provider
//   if ((window?.ethereum as any)?.isTrust) {
//     return 'trustWallet';
//   }
//   // Metamask (ethereum.isMetaMask) or other
//   // https://docs.metamask.io/guide/mobile-best-practices.html#the-provider-window-ethereum
//   // const web3 = await getWeb3Async('softwareWeb3');
//   if (!window?.ethereum && !window.web3?.currentProvider) {
//     return false;
//   }
//   return 'softwareWeb3';
// };

// https://medium.com/metamask/https-medium-com-metamask-breaking-change-injecting-web3-7722797916a8
export const getWeb3Async = async (
  walletType: WalletType
): Promise<BrowserProvider | undefined> => {
  if (web3 && web3.walletType !== walletType) {
    await resetWeb3();
  }
  if (web3) {
    return web3.provider;
  }

  const ethereumProvider = await getEthereumProvider(walletType);
  if (ethereumProvider) {
    //  Modern dapp browsers...
    web3 = {
      walletType,
      provider: new BrowserProvider(ethereumProvider as any),
    };

    // Refresh web3 after chain was changed manually
    if (ethereumProvider?.on) {
      ethereumProvider.on('accountsChanged', (accounts: string[]) => {
        const firstWallet = accounts[0];
        if (firstWallet) {
          staticDispatch(updatePendingWalletAddress(firstWallet));
        }
      });

      ethereumProvider.on('chainChanged', (chainId: number) => {
        staticDispatch(changeWalletChainId(chainId));
        console.log(
          '[WalletChainChange] Chain change detected - rebuilding for chain id ',
          chainId
        );
        detectEthereumProvider().then(newEthProvider => {
          console.log('[WalletChainChange] New provider ready');
          web3 = {
            walletType,
            provider: new BrowserProvider(newEthProvider as any),
          };
        });
      });
    }
  } else if (window.web3 && window.web3.currentProvider) {
    // Legacy dapp browsers...
    web3 = {
      walletType,
      provider: new BrowserProvider(window.web3.currentProvider),
    };
  } else {
    // Non-dapp browsers...
    console.warn('[softwareWallet] Non-Ethereum browser detected!');
    return;
  }
  checkAndAlertWeb3NetworkCompatibility();
  return web3.provider;
};

// Cache metamask permission for whole session
let haveWalletPermission = false;

/**
 * New Metamask might need to check for permission
 */
const haveOrAskWalletExtensionPermission = async (walletType: WalletType) => {
  if (haveWalletPermission) {
    console.log('[softwareWallet] using cached permission');
    return;
  }
  console.log('[softwareWallet] getting permission...');
  const web3 = await getWeb3Async(walletType);

  if (!web3) {
    throw new Error('Unable to detect wallet');
  }
  // if (!web3?.provider?.request) {
  //   // Fallback to the old way
  //   const ethProvider = await getEthereumProvider(walletType);
  //   if (
  //     !ethProvider ||
  //     !Object.prototype.hasOwnProperty.call(ethProvider, 'enable')
  //   ) {
  //     console.warn('[softwareWallet] No available web3 provider');
  //     return;
  //   }
  //   return ethProvider
  //     .enable()
  //     .then(() => {
  //       console.log('[softwareWallet] permission OK (legacy)');
  //       haveWalletPermission = true;
  //     })
  //     .catch((error: any) => {
  //       // eg. User rejected provider access
  //       // Wrap their error in Error so we get `.message` and proper stacktrace
  //       throw new Error(error?.message || error);
  //     });
  // }

  return (
    web3.provider
      .getSigner()
      // This is not needed anymore: `request({ method: 'eth_requestAccounts' })`
      .then(_accounts => {
        // console.log(accounts[0]);
        console.log('[softwareWallet] permission OK');
        haveWalletPermission = true;
      })
      .catch(error => {
        if (error?.code === 4001) {
          // EIP-1193 userRejectedRequest error
          // If this happens, the user rejected the connection request.
          console.warn(error);
          throw new Error(REJECT_WALLET_PROMPT_ERROR);
        }
        throw new Error(error?.message || error);
      })
  );
};

export const getSoftwareWalletAccount = async (
  walletType: WalletType
): Promise<string | undefined> => {
  await haveOrAskWalletExtensionPermission(walletType);
  const web3 = await getWeb3Async(walletType);
  if (!web3) {
    return;
  }
  const firstAccount = await web3.listAccounts().then(accounts => accounts[0]);
  const walletAddress = firstAccount.getAddress();
  return walletAddress;
};

export const getSigner = async (
  walletType: WalletType,
  walletAddress?: string
): Promise<ethers.JsonRpcSigner> => {
  await haveOrAskWalletExtensionPermission(walletType);
  const web3instance = await getWeb3Async(walletType);
  if (!web3instance) {
    throw Error('No software wallet detected');
  }
  return web3instance.getSigner(walletAddress);
};

// export const signText = async (text: string): Promise<string> => {
//   const signer = await getSigner();
//   return signer.signMessage(text);
// };

// export const signHash = async (hash: string): Promise<string> => {
//   const signer = await getSigner();
//   return signer.signMessage(getBytes(hash));
// };

// export const signTransaction = async (
//   transaction: ethers.providers.TransactionRequest
// ) => {
//   const web3instance = await getWeb3Async();
//   if (!web3instance) {
//     throw Error('No software wallet detected');
//   }
//   return await web3instance.getSigner().sendTransaction(transaction);
// };

// export const watchAssetInSoftwareWallet = token => {
//   if (!window.ethereum.sendAsync) {
//     return Promise.reject();
//   }
//   return window.ethereum.sendAsync({
//     method: 'wallet_watchAsset',
//     params: {
//       type: 'ERC20',
//       options: {
//         address: token.address,
//         symbol: token.symbol,
//         decimals: token.decimals
//         // image: tokenImage
//       }
//     }
//   });
// };
